/* eslint-disable @typescript-eslint/no-floating-promises */

import Image from 'next/image'
import assetLoader from '@utils/internalImageLoader'
import PropertyControlledComponent from '@components/PropertyControlledComponent/PropertyControlledComponent'
import { memo, useEffect, useRef, useState } from 'react'
import { fastagBillerService } from '@services/fastagBillerService'
import { popularFastagProviders } from '@constants/fastagProviders'
import { ModalType } from '@enums/ModalType'
import { OVERLAY_MODAL_DEFAULT_CROSS_ICON_SRC } from '@constants/crossIcon'
import { Overflow } from '@enums/OverflowTypes'
import { FASTagProvidersScreenProps, Provider } from '@interfaces/fastag'
import styles from './fastagProvidersScreen.module.scss'
import { BANK_LOGO, FILTER_BANK, ICONS, SELECT_FASTAG_PROVIDER } from '../FastagWidget/constants'
import { ARROW_RIGHT_URL } from '../ChallanWidgetLanding/constants'
import BankListShimmer from '../FastagWidget/bankListShimmer'
import {
  BBPS_LOGO_SRC,
  BLACK_BACK_ICON,
  NO_RESULTS,
  OTHER_PROVIDERS,
  POPULAR_PROVIDERS,
  SEARCH_FASTAG_PROVIDER_PLACEHOLDER,
} from './constants'

const FASTagProvidersScreen: React.FC<FASTagProvidersScreenProps> = memo(
  ({ isMobile, showProvidersScreen, setShowProvidersScreen, onSelect }) => {
    const [fastagProviders, setFastagProviders] = useState<Provider[]>([])
    const [isFetchingProviders, setIsFetchingProviders] = useState<boolean>(true)
    const [filteredProviders, setFilteredProviders] = useState<Provider[]>([])
    const [popularProvidersList, setPopularProvidersList] = useState<Provider[]>([])
    const [otherProvidersList, setOtherProvidersList] = useState<Provider[]>([])
    const [searchBankInput, setSearchBankInput] = useState<string>('')
    const [isInputFocused, setIsInputFocused] = useState<boolean>(false)
    const modalRef = useRef<HTMLDivElement>(null)

    const segregateProviders = data => {
      const matchedList: Provider[] = []
      const unmatchedList: Provider[] = []
      data?.forEach(item => {
        if (popularFastagProviders[item.name]) {
          // eslint-disable-next-line no-param-reassign
          item.name = popularFastagProviders[item.name]
          matchedList.push(item)
        } else unmatchedList.push(item)
      })
      return { matchedList, unmatchedList }
    }

    useEffect(() => {
      const fetchData = async () => {
        const data = FILTER_BANK(await fastagBillerService(setIsFetchingProviders), searchBankInput)
        if (data.length !== 0) {
          const { matchedList, unmatchedList } = segregateProviders(data)
          setFastagProviders(data)
          setPopularProvidersList(matchedList)
          setOtherProvidersList(unmatchedList)
        }
      }
      fetchData()
    }, [])

    useEffect(() => {
      setFilteredProviders(FILTER_BANK(fastagProviders, searchBankInput))
    }, [searchBankInput])

    useEffect(() => {
      if (showProvidersScreen) {
        document.documentElement.style.overflowY = Overflow.Hidden
      }
    }, [showProvidersScreen])

    const onBankSearch = e => {
      if (!e.target.value) setIsInputFocused(false)
      else setIsInputFocused(true)
      setSearchBankInput(e.target.value)
    }

    const handleClick = selectedProvider => {
      onSelect(selectedProvider)
      document.documentElement.style.overflowY = Overflow.Scroll
    }
    const inputRef = useRef<any>(null)

    const onCloseModal = () => {
      setShowProvidersScreen(false)
      document.documentElement.style.overflowY = Overflow.Scroll
    }

    const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
      if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
        onCloseModal()
      }
    }

    const getListedBanks = (banksList: Provider[], heading?: string) =>
      banksList.length ? (
        <div className={styles.providersList}>
          <span>{heading}</span>
          <div className={styles.list}>
            {banksList?.map((bank, idx) => (
              <>
                <div key={bank?.biller_details_id} className={styles.bankDiv} onClick={() => handleClick(bank)}>
                  <div className={styles.leftContent}>
                    <span>
                      <Image src={bank?.icon_url} width={24} height={24} alt={BANK_LOGO} />
                    </span>
                    {bank?.name}
                  </div>
                  <span>
                    <Image src={ARROW_RIGHT_URL} width={20} height={20} alt={BANK_LOGO} />
                  </span>
                </div>
                {banksList.length !== idx + 1 && <div className={styles.line} />}
              </>
            ))}
          </div>
        </div>
      ) : null

    return (
      <div className={`${!isMobile ? styles.overlay : ''}`} onClick={handleOverlayClick}>
        <div className={`${!isMobile ? styles.modal : ''}`} ref={modalRef}>
          <Image
            src={OVERLAY_MODAL_DEFAULT_CROSS_ICON_SRC}
            alt="Overlay cross Icon"
            width={24}
            height={24}
            loading="lazy"
            onClick={onCloseModal}
            className={styles.crossIcon}
          />
          <div className={styles.fastagProvidersMain}>
            <div className={styles.fastagProviderHeader}>
              <Image src={BLACK_BACK_ICON} alt="Back icon" width={12} height={24} onClick={onCloseModal} />
              <Image src={BBPS_LOGO_SRC} alt="BBPS logo" width={56} height={20} className={styles.bbpsLogo} />
            </div>
            <div className={styles.popularFastagProvidersection}>
              <div className={styles.topSection}>
                <div className={styles.heading}>{SELECT_FASTAG_PROVIDER}</div>
                <div className={styles.fastagInputContainer}>
                  <div className={styles.fastagInputSearchImg}>
                    <PropertyControlledComponent
                      fallback={<Image src={`${assetLoader()}${ICONS.search}`} alt="search" width={20} height={20} />}
                      controllerProperty={isInputFocused}>
                      <Image
                        src={BLACK_BACK_ICON}
                        className={styles.inputArrow}
                        alt="search"
                        width={16}
                        height={16}
                        onClick={() => {
                          setSearchBankInput('')
                          setIsInputFocused(false)
                          inputRef.current.blur()
                        }}
                      />
                    </PropertyControlledComponent>
                  </div>
                  <input
                    type="text"
                    value={searchBankInput}
                    className={`${isInputFocused ? styles.inputOnFocus : ''}`}
                    onChange={e => onBankSearch(e)}
                    placeholder={SEARCH_FASTAG_PROVIDER_PLACEHOLDER}
                    ref={inputRef}
                    onFocus={() => setIsInputFocused(true)}
                  />
                  <PropertyControlledComponent fallback={null} controllerProperty={searchBankInput}>
                    <div
                      className={styles.fastagInputCross}
                      onClick={() => {
                        setSearchBankInput('')
                        setIsInputFocused(true)
                        inputRef.current.focus()
                      }}>
                      <Image src={`${assetLoader()}${ICONS.close}`} alt="search" width={20} height={20} />
                    </div>
                  </PropertyControlledComponent>
                </div>
              </div>
              <div className={styles.providerListContainer}>
                <PropertyControlledComponent
                  controllerProperty={!isInputFocused || !searchBankInput}
                  fallback={
                    <PropertyControlledComponent
                      controllerProperty={filteredProviders.length !== 0}
                      fallback={<span className={styles.noResults}>{NO_RESULTS}</span>}>
                      {getListedBanks(filteredProviders)}
                    </PropertyControlledComponent>
                  }>
                  {isFetchingProviders ? (
                    <BankListShimmer shimmerNumber={6} shimmerSize={ModalType.LARGE} />
                  ) : (
                    getListedBanks(popularProvidersList, POPULAR_PROVIDERS)
                  )}
                  {getListedBanks(otherProvidersList, OTHER_PROVIDERS)}
                </PropertyControlledComponent>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }
)

export default FASTagProvidersScreen
